/**
 * 2D schematic elevation / sectional view of a tower structure.
 *
 * This is a deliberately "engineering drawing" style renderer: a clean,
 * orthographic side-on 2D projection of the physics `Structure`, drawn with the
 * HTML Canvas 2D API (no Three.js / WebGL). Every member is a single line,
 * color-coded by utilization, so at a glance you can read where the tower is
 * working hard and where it is about to fail — exactly like the coloured stress
 * plots on a structural elevation.
 *
 * Coordinate convention (matches the physics model, SI units, metres):
 *   - y is UP in the world; we invert on screen so up is up.
 *   - `front` projection looks along +Z, using world (x, y).
 *   - `side`  projection looks along +X, using world (z, y).
 *   - `top`   projection looks DOWN along +Y (plan), using world (x, z).
 *
 * The projection scale/offset ("fit") is computed ONCE from the *undeformed*
 * structure bounds (captured at setStructure) and reused every frame. That way
 * the drawing stays rock-steady while the tower leans and collapses, instead of
 * the auto-fit chasing the falling debris around the canvas.
 *
 * On top of the stable auto-fit sits an interactive VIEW transform (zoom +
 * pan). Zoom/pan never touch the fit; they are an extra scale-about-cursor and
 * translate applied at paint time, so "reset" cleanly returns to the auto-fit
 * and a collapse still doesn't make the drawing jump.
 *
 * The panel can also be EXPANDED to fill the left half of the screen (click the
 * background or the ⤢ button) and collapsed back to its corner. Expansion is
 * done purely with inline styles on the container (which override the
 * stylesheet), so this component never has to touch style.css.
 */

import { MATERIALS, type Member, type Structure } from "@/physics/model";

type Projection = "front" | "side" | "top";

interface Schematic2DOptions {
  onMemberClick?: (memberIndex: number | null) => void;
  onDrawMember?: (from: { x: number; y: number }, to: { x: number; y: number }) => void;
  onSelectBox?: (from: { x: number; y: number }, to: { x: number; y: number }, add: boolean) => void;
  onSelectPoint?: (point: { x: number; y: number }, add: boolean) => void;
  onMoveSelection?: (delta: { x: number; y: number }) => void;
  /** Called when a move-drag starts, to grab (and select) the joint/segment there. */
  onGrabForMove?: (point: { x: number; y: number }) => void;
  canStartMemberAt?: (point: { x: number; y: number }) => boolean;
  canMoveNodeAt?: (point: { x: number; y: number }) => boolean;
  isDrawEnabled?: () => boolean;
  getEditCursor?: () => string;
  getEditOverlay?: () => SchematicEditOverlay | null;
  /** True only while the Simulate pane is active — gates stress colouring. */
  isSimulating?: () => boolean;
  // Copy-paste placement: a pasted part floating under the cursor (see freeBuild).
  onPlacementMove?: (x: number, y: number) => void;
  onPlacementCommit?: () => void;
  onPlacementRotate?: () => void;
  onPlacementScale?: (factor: number) => void;
  onPlacementCancel?: () => void;
}

interface SchematicEditOverlay {
  nodes: Array<{ id: number; x: number; y: number; selected: boolean; fixed: boolean }>;
  members: Array<{ id: number; a: number; b: number; section: string; material?: string; selected: boolean }>;
  mirror?: { enabled: boolean; axisX: number };
  placement?: {
    members: Array<{ x1: number; y1: number; x2: number; y2: number; section: string }>;
    nodes: Array<{ x: number; y: number }>;
    scale: number;
    angleDeg: number;
  } | null;
}

// The single source of truth for member colour BY SECTION TYPE — shared by the
// structure render, the edit overlay, and the legend so a colour always means the
// same thing while you're designing (blue pipe, green brace/angle, yellow rod,
// red guy/cable, steel flat). Stress (green→red) only replaces this while simulating.
const PIPE_COL = "rgba(111, 168, 220, 0.96)"; // blue
const ANGLE_COL = "rgba(147, 196, 125, 0.96)"; // green
const ROD_COL = "rgba(255, 217, 102, 0.98)"; // yellow
const GUY_COL = "rgba(224, 102, 102, 0.98)"; // red

// Distinct colour per MATERIAL, so a leg of aluminium reads differently from one
// of steel while designing. Curated by family (steels blue, strand amber, wood
// tan, composites dark/teal, novelty pastel). Section type is still conveyed by
// stroke STYLE, so colour is free to mean material.
const MATERIAL_HEX: Record<string, string> = {
  a36: "#6f9bd6",
  a572: "#5a86c8",
  a992: "#87b0e0",
  guyStrand: "#d98c5a",
  aramid: "#e0c05a",
  al6061: "#cdd5df",
  ss304: "#7fd0d6",
  ti6al4v: "#b48cd6",
  wroughtIron: "#b08d6a",
  castIron: "#899",
  cfrp: "#464b54",
  gfrp: "#6fc0a8",
  douglasFir: "#caa06a",
  oak: "#b0824a",
  bamboo: "#9bc46a",
  spaghetti: "#e6cf8f",
  greenWillow: "#8fce7a",
  ice: "#a8d8f0",
  glass: "#bfe0e8",
};
const MATERIAL_COLOR: Record<string, string> = {};
for (const k of Object.keys(MATERIALS) as Array<keyof typeof MATERIALS>) {
  MATERIAL_COLOR[MATERIALS[k].name] = MATERIAL_HEX[k] ?? "#c8d2de";
}
function materialColor(name: string): string {
  return MATERIAL_COLOR[name] ?? "#c8d2de";
}
function materialColorByKey(key: string): string {
  return MATERIAL_HEX[key] ?? "#c8d2de";
}

function overlaySectionColor(section: string): string {
  // Match the free-build palette so legs/braces/rods/guys read consistently.
  if (section.startsWith("leg")) return PIPE_COL;
  if (section.startsWith("brace")) return ANGLE_COL;
  if (section.startsWith("rod")) return ROD_COL;
  if (section.startsWith("guy")) return GUY_COL;
  return "rgba(210, 220, 230, 0.9)";
}

/**
 * Steel section family, inferred from `section.name` (with `tensionOnly` as the
 * authority for guys). Each family is drawn with a distinguishable STROKE STYLE
 * — line cap + optional edge stroke + dash — while colour stays reserved for
 * utilization. See `classifySection` and `drawMembers`.
 */
type SectionType = "pipe" | "angle" | "flat" | "rod" | "guy";

interface SectionStyle {
  label: string;
  /** Canvas line cap that reads as the section's end/profile. */
  cap: CanvasLineCap;
  /** Dash pattern in device px (empty = solid). Multiplied by dpr at draw. */
  dash: number[];
  /**
   * If true, draw a thin secondary "edge" stroke offset alongside the member so
   * an angle reads as an L / two-faced profile rather than a plain bar.
   */
  edge: boolean;
}

/** Static per-type drawing recipe. Colour is applied separately (utilization). */
const SECTION_STYLES: Record<SectionType, SectionStyle> = {
  // Tubular: fully round-capped, solid — the smooth "pipe" read.
  pipe: { label: 'Pipe (□)', cap: "round", dash: [], edge: false },
  // Angle (L): solid, square-capped, with a parallel edge stroke = two faces.
  angle: { label: "Angle L", cap: "butt", dash: [], edge: true },
  // Flat bar: solid, hard butt caps — a plain rectangular strap.
  flat: { label: "Flat bar", cap: "butt", dash: [], edge: false },
  // Round rod: solid, thin, round-capped (thinness set by the width mapping).
  rod: { label: "Rod ø", cap: "round", dash: [], edge: false },
  // Guy cable: thin dashed line — obviously a flexible tension element.
  guy: { label: "Guy cable", cap: "round", dash: [5, 4], edge: false },
};

const SECTION_TYPE_ORDER: SectionType[] = ["pipe", "angle", "flat", "rod", "guy"];

/**
 * Classify a member into a steel-section family. `tensionOnly` is the ground
 * truth for guys; otherwise we parse the section name prefix. Falls back to
 * "flat" (a neutral solid bar) if a name is unrecognised.
 */
function classifySection(mem: Member): SectionType {
  if (mem.tensionOnly) return "guy";
  const n = mem.section.name;
  if (n.includes("EHS")) return "guy";
  if (n.startsWith("Pipe")) return "pipe";
  if (n.startsWith("Ø")) return "rod";
  if (n.startsWith("Flat")) return "flat";
  // Angle: "L" immediately followed by a digit (e.g. L3x3x1/4).
  if (n.length >= 2 && n[0] === "L" && n[1]! >= "0" && n[1]! <= "9") return "angle";
  return "flat";
}

/**
 * Exaggerated thickness mapping: real section width (`dragWidth`, metres) → a
 * base line width in device-independent px, so heavy steel reads MUCH thicker
 * than light bracing or guys.
 *
 * We normalise dragWidth across the realistic span (~0.005 m guys up to
 * ~0.22 m 8" pipe) and push it through a mild power curve that widens the gap
 * between light and heavy, then map onto a generous [min, max] px band. Guys
 * are held to the thin end regardless so cables never read as structure.
 */
const DRAG_MIN = 0.005; // ~thinnest guy
const DRAG_MAX = 0.22; // ~fattest pipe (8")
const WIDTH_MIN_PX = 0.9; // thinnest drawn member (guys/rods)
const WIDTH_MAX_PX = 9.0; // fattest drawn member (heavy pipe), before util/zoom
const WIDTH_POWER = 1.6; // >1 exaggerates the light→heavy spread

function sectionBaseWidthPx(dragWidth: number, type: SectionType): number {
  const t = (dragWidth - DRAG_MIN) / (DRAG_MAX - DRAG_MIN);
  const clamped = Math.max(0, Math.min(1, t));
  const curved = Math.pow(clamped, WIDTH_POWER);
  let px = WIDTH_MIN_PX + curved * (WIDTH_MAX_PX - WIDTH_MIN_PX);
  // Keep guys unambiguously thin no matter how the curve lands.
  if (type === "guy") px = Math.min(px, WIDTH_MIN_PX + 0.6);
  return px;
}

function pointSegmentDistance(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 <= 1e-9) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2));
  return Math.hypot(px - (x1 + dx * t), py - (y1 + dy * t));
}

interface FitMeta {
  height?: number;
  halfWidth?: number;
  foundation?: import("@/game/towerSpec").BuiltTower["foundation"];
}

/** World bounds of the undeformed structure in the projected plane (metres). */
interface Bounds {
  minH: number; // horizontal (x for front, z for side/plan-x for top)
  maxH: number;
  minV: number; // vertical (y for front/side; z for top)
  maxV: number;
}

// --- palette (dark engineering UI) ---
const BG = "#0b0e13";
const GRID = "rgba(120, 140, 165, 0.10)";
const GROUND = "rgba(150, 170, 190, 0.55)";
const TICK_TEXT = "rgba(170, 190, 210, 0.75)";
const TITLE_TEXT = "rgba(200, 218, 235, 0.9)";
const SLACK_GUY = "rgba(120, 150, 190, 0.5)";
const FAILED = "#7a1c1c";

const MARGIN_PX = 42; // drawing margin around the tower (device-independent px)
const RULER_SIDE_GUTTER_PX = 52;
const RULER_TOP_GUTTER_PX = 24;
const RULER_BOTTOM_GUTTER_PX = 40;
const FIT_LOOSEN_FACTOR = 0.88;

// Drawing aids (front-view editing): snap grid + cursor readout.
const SNAP_STEP_M = 1; // mirrors the free-draw snap grid (freeBuild GRID)
const DRAW_ACCENT = "rgba(125, 211, 252, 0.95)"; // cyan — the "you are drawing" colour
const SNAP_DOT = "rgba(125, 211, 252, 0.18)";

const ZOOM_MIN = 0.12;
const ZOOM_MAX = 8;
const DEBUG_PREFIX = "[schematic2d]";

const PROJECTIONS: Projection[] = ["front", "side", "top"];

/**
 * Utilization → colour ramp: green (relaxed) → yellow (~0.6) → orange → red
 * (>=1, at capacity). Kept as a pure function so it can also feed the legend.
 */
function utilColor(util: number): string {
  const u = Math.max(0, Math.min(1, util));
  // Piecewise HSL hue sweep: 130deg (green) down to 0deg (red).
  // Compress the top end so the transition to red is visually urgent.
  let hue: number;
  if (u < 0.6) {
    // 130 (green) -> 55 (yellow)
    hue = 130 - (u / 0.6) * 75;
  } else {
    // 55 (yellow) -> 0 (red)
    hue = 55 - ((u - 0.6) / 0.4) * 55;
  }
  const light = 52 + u * 6; // brighten slightly as it gets hot
  return `hsl(${hue.toFixed(0)}, 85%, ${light.toFixed(0)}%)`;
}

function pickTickStep(span: number, targetTicks: number): number {
  const raw = Math.max(span / Math.max(1, targetTicks), 0.1);
  const pow10 = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / pow10;
  const nice = unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10;
  return nice * pow10;
}

function trimTick(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export class Schematic2D {
  private readonly container: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly onMemberClick?: (memberIndex: number | null) => void;
  private readonly onDrawMember?: (from: { x: number; y: number }, to: { x: number; y: number }) => void;
  private readonly onSelectBox?: (from: { x: number; y: number }, to: { x: number; y: number }, add: boolean) => void;
  private readonly onSelectPoint?: (point: { x: number; y: number }, add: boolean) => void;
  private readonly onMoveSelection?: (delta: { x: number; y: number }) => void;
  private readonly onGrabForMove?: (point: { x: number; y: number }) => void;
  private readonly canStartMemberAt: (point: { x: number; y: number }) => boolean;
  private readonly canMoveNodeAt: (point: { x: number; y: number }) => boolean;
  private readonly isDrawEnabled: () => boolean;
  private readonly getEditCursor: () => string;
  private readonly getEditOverlay: () => SchematicEditOverlay | null;
  private readonly isSimulating: () => boolean;
  private readonly onPlacementMove?: (x: number, y: number) => void;
  private readonly onPlacementCommit?: () => void;
  private readonly onPlacementRotate?: () => void;
  private readonly onPlacementScale?: (factor: number) => void;
  private readonly onPlacementCancel?: () => void;

  /** Overlay layer holding the HTML control buttons (projection/zoom/expand). */
  private readonly ui: HTMLDivElement;
  private projLabelBtn!: HTMLButtonElement;
  private expandBtn!: HTMLButtonElement;

  private structure: Structure | null = null;
  private meta: FitMeta = {};
  private projection: Projection = "front";

  /** Undeformed bounds captured at setStructure — the fit reference. */
  private bounds: Bounds | null = null;

  // Cached projection transform (world metres -> device pixels), recomputed on
  // setStructure / resize only, so the view is stable during a collapse.
  private scale = 1; // device-px per metre (auto-fit only)
  private offH = 0; // device-px offset for horizontal axis (auto-fit only)
  private offV = 0; // device-px offset for vertical axis (auto-fit only)
  private dpr = 1;
  private cssW = 0;
  private cssH = 0;

  // Interactive view transform, layered on top of the auto-fit. Applied in
  // DEVICE pixels: screen = (fitPixel - center) * zoom + center + pan.
  // With zoom = 1 and pan = 0 the view is exactly the auto-fit.
  private zoom = 0.92;
  private panX = 0; // device-px
  private panY = 0; // device-px

  /** Whether the panel is currently expanded to fill the left half of screen. */
  private expanded = false;
  private mainMode = false;
  /** Inline style keys we set on expand, so collapse can cleanly clear them. */
  private static readonly EXPAND_KEYS = [
    "left",
    "top",
    "right",
    "bottom",
    "width",
    "height",
    "zIndex",
  ] as const;

  // Drag-to-pan bookkeeping.
  private dragging = false;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private movedDuringDrag = false;
  private selectedMemberIdx: number | null = null;
  private drawStart: { x: number; y: number } | null = null;
  private drawCurrent: { x: number; y: number } | null = null;
  private hoverWorld: { x: number; y: number } | null = null; // cursor pos while editing
  private infoTip: { sx: number; sy: number; lines: string[]; color: string } | null = null;
  private drawSnapEnd: { x: number; y: number } | null = null; // angle/parallel-snapped draw end
  private drawSnapHint: string | null = null; // "horizontal" | "vertical" | "45°" | "parallel"
  private editGesture: "draw" | "select" | "move" | null = null;
  private editAddSelection = false;

  // Bound listeners (kept so dispose() can remove them).
  private readonly onWheel = (e: WheelEvent) => this.handleWheel(e);
  private readonly onPointerDown = (e: PointerEvent) => this.handlePointerDown(e);
  private readonly onPointerMove = (e: PointerEvent) => this.handlePointerMove(e);
  private readonly onPointerUp = (e: PointerEvent) => this.handlePointerUp(e);
  private readonly onKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
  private readonly onBackgroundClick = (e: MouseEvent) => this.handleBackgroundClick(e);

  constructor(container: HTMLElement, opts: Schematic2DOptions = {}) {
    this.container = container;
    this.onMemberClick = opts.onMemberClick;
    this.onDrawMember = opts.onDrawMember;
    this.onSelectBox = opts.onSelectBox;
    this.onSelectPoint = opts.onSelectPoint;
    this.onMoveSelection = opts.onMoveSelection;
    this.onGrabForMove = opts.onGrabForMove;
    this.canStartMemberAt = opts.canStartMemberAt ?? (() => true);
    this.canMoveNodeAt = opts.canMoveNodeAt ?? (() => false);
    this.isDrawEnabled = opts.isDrawEnabled ?? (() => false);
    this.isSimulating = opts.isSimulating ?? (() => false);
    this.onPlacementMove = opts.onPlacementMove;
    this.onPlacementCommit = opts.onPlacementCommit;
    this.onPlacementRotate = opts.onPlacementRotate;
    this.onPlacementScale = opts.onPlacementScale;
    this.onPlacementCancel = opts.onPlacementCancel;
    this.getEditCursor = opts.getEditCursor ?? (() => "grab");
    this.getEditOverlay = opts.getEditOverlay ?? (() => null);
    // The overlay + expand affordance need a positioning context. #schematic is
    // position:absolute in the stylesheet, but guard in case that ever changes.
    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative";
    }

    const canvas = document.createElement("canvas");
    canvas.style.display = "block";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    // Let the panel own background; painting BG ourselves keeps it crisp.
    canvas.style.background = BG;
    canvas.style.cursor = "grab";
    canvas.tabIndex = 0;
    container.appendChild(canvas);
    this.canvas = canvas;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Schematic2D: 2D canvas context unavailable");
    this.ctx = ctx;

    this.ui = this.buildControls();
    container.appendChild(this.ui);

    // Interactions.
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("keydown", this.onKeyDown);
    // Click on the panel background (canvas) toggles expand — unless it was a
    // pan drag (handled in the click handler by inspecting movedDuringDrag).
    canvas.addEventListener("click", this.onBackgroundClick);

    this.resize();
  }

  // --- public API --------------------------------------------------------

  setStructure(structure: Structure, meta: FitMeta = {}): void {
    this.structure = structure;
    this.meta = meta;
    this.infoTip = null; // the graph changed — a stale material tip may point at a deleted member
    this.bounds = this.computeBounds(structure, meta);
    this.recomputeFit();
    this.update();
  }

  setProjection(view: Projection): void {
    if (view === this.projection) return;
    this.projection = view;
    // Horizontal/vertical extent differs between projections, so refit.
    if (this.structure) this.bounds = this.computeBounds(this.structure, this.meta);
    // A new projection is a new frame of reference; reset the interactive view
    // so we don't inherit a pan that made sense for the previous axes.
    this.resetView();
    this.recomputeFit();
    this.syncControls();
    this.update();
  }

  resize(): void {
    this.dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    // Fall back to container size; if it hasn't been laid out yet, use a sane
    // default so the first paint isn't zero-sized.
    const rect = this.container.getBoundingClientRect();
    this.cssW = Math.max(1, Math.round(rect.width) || 320);
    this.cssH = Math.max(1, Math.round(rect.height) || 480);

    this.canvas.width = Math.round(this.cssW * this.dpr);
    this.canvas.height = Math.round(this.cssH * this.dpr);

    this.recomputeFit();
    this.update();
  }

  update(): void {
    const { ctx } = this;
    const W = this.canvas.width;
    const H = this.canvas.height;
    if (W === 0 || H === 0) return;

    // Clear (we redraw everything each frame — cheap for a few hundred lines).
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);

    this.drawGridAndTicks();
    this.drawSnapGrid();
    this.drawGround();
    this.drawFoundation();

    const s = this.structure;
    if (s) this.drawMembers(s);
    this.drawEditOverlay();
    this.drawMemberPreview();
    this.drawCursorReadout();
    this.drawPlacementGhost();
    this.drawInfoTip();

    this.drawTitle();
    this.drawLegend();
    this.drawSectionLegend();
    this.drawMaterialLegend();
    this.syncCursor();
  }

  dispose(): void {
    this.canvas.removeEventListener("wheel", this.onWheel);
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("keydown", this.onKeyDown);
    this.canvas.removeEventListener("click", this.onBackgroundClick);

    if (this.ui.parentNode === this.container) {
      this.container.removeChild(this.ui);
    }
    if (this.canvas.parentNode === this.container) {
      this.container.removeChild(this.canvas);
    }
    // Clear any inline expand styles we may have applied.
    if (this.expanded) this.applyCollapsedStyles();

    this.structure = null;
    this.bounds = null;
  }

  // --- interactive view: expand / zoom / pan -----------------------------

  /** Toggle the large left-half layout. */
  toggleExpand(): void {
    this.setExpanded(!this.expanded);
  }

  setExpanded(expanded: boolean): void {
    if (expanded === this.expanded) return;
    this.expanded = expanded;
    if (expanded) this.applyExpandedStyles();
    else this.applyCollapsedStyles();
    this.syncControls();
    // Re-measure at the new size and re-fit so the drawing fills the panel.
    this.resize();
  }

  setMainMode(main: boolean): void {
    if (main === this.mainMode) return;
    this.mainMode = main;
    if (main && this.expanded) this.setExpanded(false);
    this.syncControls();
    this.update();
  }

  private applyExpandedStyles(): void {
    const s = this.container.style;
    s.left = "16px";
    s.top = "16px";
    s.right = "auto";
    s.bottom = "auto";
    s.width = "calc(100vw - 32px)";
    s.height = "calc(100vh - 32px)";
    s.zIndex = "50";
  }

  private applyCollapsedStyles(): void {
    const s = this.container.style;
    // Removing the inline values lets the stylesheet defaults take over again.
    for (const k of Schematic2D.EXPAND_KEYS) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (s as any)[k] = "";
    }
  }

  /** Reset zoom/pan to the auto-fit view. */
  resetView(): void {
    this.zoom = 0.92;
    this.panX = 0;
    this.panY = 0;
  }

  setSelectedMember(memberIndex: number | null): void {
    this.selectedMemberIdx = memberIndex;
    this.update();
  }

  focus(): void {
    this.canvas.focus();
  }

  /** Multiply zoom about the panel centre (used by +/- buttons). */
  private zoomBy(factor: number): void {
    const cx = this.canvas.width / 2;
    const cy = this.canvas.height / 2;
    this.zoomAbout(factor, cx, cy);
    this.update();
  }

  /**
   * Multiply zoom by `factor`, keeping the DEVICE-pixel point (ax, ay) fixed on
   * screen. Clamped to [ZOOM_MIN, ZOOM_MAX]; pan is adjusted to preserve the
   * anchor so wheel-zoom homes in on the cursor.
   */
  private zoomAbout(factor: number, ax: number, ay: number): void {
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.zoom * factor));
    const applied = next / this.zoom;
    if (applied === 1) return;
    const cx = this.canvas.width / 2;
    const cy = this.canvas.height / 2;
    // screen = (fit - c) * zoom + c + pan. Hold (ax,ay) fixed as zoom scales.
    this.panX = ax - applied * (ax - cx - this.panX) - cx;
    this.panY = ay - applied * (ay - cy - this.panY) - cy;
    this.zoom = next;
  }

  /** A pasted part is floating under the cursor, ready to place (front view only). */
  private isPlacing(): boolean {
    // isDrawEnabled() is checked first: it's cheap AND avoids touching getEditOverlay
    // (→ freeBuilder) during the constructor's first paint, before that binding exists.
    return this.isDrawEnabled() && this.projection === "front" && !!this.getEditOverlay()?.placement;
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    // While placing a pasted part, the wheel scales it instead of zooming.
    if (this.isPlacing()) {
      this.onPlacementScale?.(Math.exp(-e.deltaY * 0.0015));
      this.update();
      return;
    }
    const rect = this.canvas.getBoundingClientRect();
    const ax = (e.clientX - rect.left) * this.dpr;
    const ay = (e.clientY - rect.top) * this.dpr;
    // Smooth exponential zoom; wheel up (negative deltaY) zooms in.
    const factor = Math.exp(-e.deltaY * 0.0015);
    this.zoomAbout(factor, ax, ay);
    this.update();
  }

  private handlePointerDown(e: PointerEvent): void {
    if (e.button !== 0 && e.button !== 1) return;
    e.preventDefault();
    this.canvas.focus();
    this.infoTip = null; // a new gesture dismisses the last material tip
    // Placing a pasted part: left-click drops it, middle-click rotates it 90°.
    if (this.isPlacing()) {
      if (e.button === 1) this.onPlacementRotate?.();
      else this.onPlacementCommit?.();
      this.update();
      return;
    }
    if (e.button === 0 && this.canEdit()) {
      const w = this.worldFromClient(e.clientX, e.clientY);
      if (!w) return;
      this.drawStart = w;
      this.drawCurrent = w;
      this.editGesture = this.canMoveNodeAt(w) ? "move" : this.canStartMemberAt(w) ? "draw" : "select";
      // Grabbing a joint/segment to move it also selects it (so a single drag moves
      // whatever is under the cursor, without a separate click-to-select first).
      if (this.editGesture === "move") this.onGrabForMove?.(w);
      this.editAddSelection = e.shiftKey;
      this.dragging = true;
      this.movedDuringDrag = false;
      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;
      this.canvas.style.cursor = this.editGesture === "draw" ? "crosshair" : this.editGesture === "move" ? "move" : "cell";
      console.info(DEBUG_PREFIX, "pointer down", {
        gesture: this.editGesture,
        point: w,
        drawEnabled: this.isDrawEnabled(),
      });
      return;
    }
    this.dragging = true;
    this.movedDuringDrag = false;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
    this.canvas.style.cursor = "grabbing";
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.dragging) {
      const overCanvas = e.target === this.canvas;
      // A floating pasted part follows the cursor.
      if (this.isPlacing() && overCanvas) {
        const w = this.worldFromClient(e.clientX, e.clientY);
        if (w) {
          this.onPlacementMove?.(w.x, w.y);
          this.update();
        }
        return;
      }
      // Otherwise track the cursor over the canvas while editing (snap position).
      if (this.canEdit() && overCanvas) {
        const w = this.worldFromClient(e.clientX, e.clientY);
        if (w) {
          this.hoverWorld = w;
          this.update();
        }
      } else if (this.hoverWorld) {
        this.hoverWorld = null;
        this.update();
      }
      return;
    }
    if (this.drawStart) {
      const w = this.worldFromClient(e.clientX, e.clientY);
      if (w) {
        if (Math.abs(e.clientX - this.lastPointerX) > 2 || Math.abs(e.clientY - this.lastPointerY) > 2) {
          this.movedDuringDrag = true;
        }
        this.drawCurrent = w;
        if (this.editGesture === "draw") {
          const s = this.snapDrawEnd(this.drawStart, w);
          this.drawSnapEnd = s.end;
          this.drawSnapHint = s.hint;
        } else {
          this.drawSnapEnd = null;
          this.drawSnapHint = null;
        }
        this.update();
      }
      return;
    }
    const dx = e.clientX - this.lastPointerX;
    const dy = e.clientY - this.lastPointerY;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.movedDuringDrag = true;
    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;
    this.panX += dx * this.dpr;
    this.panY += dy * this.dpr;
    this.update();
  }

  private handlePointerUp(_e: PointerEvent): void {
    if (!this.dragging) return;
    if (this.drawStart) {
      const from = this.drawStart;
      // Use the angle/parallel-snapped end for a draw, so lines land straight.
      const to = this.editGesture === "draw" && this.drawSnapEnd ? this.drawSnapEnd : this.drawCurrent;
      const gesture = this.editGesture;
      const add = this.editAddSelection;
      this.drawStart = null;
      this.drawCurrent = null;
      this.drawSnapEnd = null;
      this.drawSnapHint = null;
      this.editGesture = null;
      this.dragging = false;
      this.syncCursor();
      console.info(DEBUG_PREFIX, "pointer up", {
        gesture,
        from,
        to,
        moved: this.movedDuringDrag,
        distance: to ? Math.hypot(to.x - from.x, to.y - from.y) : null,
      });
      if (to && this.movedDuringDrag && gesture === "draw" && Math.hypot(to.x - from.x, to.y - from.y) > 0.1) {
        this.onDrawMember?.(from, to);
      } else if (to && this.movedDuringDrag && gesture === "select") {
        this.onSelectBox?.(from, to, add);
      } else if (to && this.movedDuringDrag && gesture === "move") {
        this.onMoveSelection?.({ x: to.x - from.x, y: to.y - from.y });
      } else if (!this.movedDuringDrag) {
        if (this.isDrawEnabled()) {
          this.onSelectPoint?.(from, add);
          // Pop a material-info tip for the member clicked (like the 3D inspector).
          this.showMemberInfoAt(_e.clientX, _e.clientY);
        } else {
          const picked = this.pickMemberAt(_e.clientX, _e.clientY);
          this.setSelectedMember(picked);
          this.onMemberClick?.(picked);
        }
      }
      this.update();
      return;
    }
    this.dragging = false;
    this.syncCursor();
  }

  private syncCursor(): void {
    if (this.dragging) return;
    this.canvas.style.cursor = this.canEdit() ? this.getEditCursor() : "grab";
  }

  /**
   * Editing (draw / move / select) is only meaningful in the FRONT projection —
   * the authored graph lives in the X–Y plane. In side/top the pointer pans/zooms
   * instead, so a drag there can't feed the z-axis in as height (which drew
   * garbage). See the hint painted in drawEditOverlay.
   */
  private canEdit(): boolean {
    return this.isDrawEnabled() && this.projection === "front";
  }

  private handleKeyDown(e: KeyboardEvent): void {
    // Esc cancels a floating paste; R rotates it 90° (in addition to middle-click).
    if (this.isPlacing()) {
      if (e.key === "Escape") {
        this.onPlacementCancel?.();
        this.update();
        e.preventDefault();
        return;
      }
      if (e.key === "r" || e.key === "R") {
        this.onPlacementRotate?.();
        this.update();
        e.preventDefault();
        return;
      }
    }
    // Esc while dragging out a line abandons it — no member is created.
    if (e.key === "Escape" && this.drawStart) {
      this.drawStart = null;
      this.drawCurrent = null;
      this.dragging = false;
      this.editGesture = null;
      this.movedDuringDrag = false;
      this.update();
      e.preventDefault();
      return;
    }
    const step = (e.shiftKey ? 60 : 24) * this.dpr;
    let used = true;
    switch (e.key) {
      case "ArrowLeft":
        this.panX += step;
        break;
      case "ArrowRight":
        this.panX -= step;
        break;
      case "ArrowUp":
        this.panY += step;
        break;
      case "ArrowDown":
        this.panY -= step;
        break;
      default:
        used = false;
        break;
    }
    if (!used) return;
    e.preventDefault();
    this.update();
  }

  private handleBackgroundClick(e: MouseEvent): void {
    // Suppress the click that ends a pan drag; only a "clean" click toggles.
    if (this.movedDuringDrag) {
      this.movedDuringDrag = false;
      return;
    }
    if (this.isDrawEnabled()) return;
    const picked = this.pickMemberAt(e.clientX, e.clientY);
    if (picked !== null) {
      this.setSelectedMember(picked);
      this.onMemberClick?.(picked);
      return;
    }
    this.setSelectedMember(null);
    this.onMemberClick?.(null);
    if (!this.mainMode) this.toggleExpand();
  }

  // --- HTML controls -----------------------------------------------------

  private buildControls(): HTMLDivElement {
    const ui = document.createElement("div");
    Object.assign(ui.style, {
      position: "absolute",
      inset: "0",
      pointerEvents: "none", // container passes clicks through to the canvas
      fontFamily: "ui-monospace, monospace",
    } as Partial<CSSStyleDeclaration>);

    // Top-right cluster: projection cycle + expand/collapse.
    const topRight = document.createElement("div");
    Object.assign(topRight.style, {
      position: "absolute",
      top: "6px",
      right: "6px",
      display: "flex",
      gap: "4px",
      pointerEvents: "auto",
    } as Partial<CSSStyleDeclaration>);

    this.projLabelBtn = this.makeButton("View: FRONT", () => this.cycleProjection());
    this.projLabelBtn.title = "Cycle projection (Front / Side / Top)";
    this.projLabelBtn.style.width = "auto";
    this.projLabelBtn.style.padding = "0 8px";

    this.expandBtn = this.makeButton("⤢", () => this.toggleExpand());
    this.expandBtn.title = "Expand / collapse panel";

    topRight.appendChild(this.projLabelBtn);
    topRight.appendChild(this.expandBtn);

    // Bottom-right cluster: zoom + / − / reset.
    const zoomBox = document.createElement("div");
    Object.assign(zoomBox.style, {
      position: "absolute",
      right: "6px",
      bottom: "6px",
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      pointerEvents: "auto",
    } as Partial<CSSStyleDeclaration>);

    const zin = this.makeButton("+", () => this.zoomBy(1.25));
    zin.title = "Zoom in";
    const zout = this.makeButton("−", () => this.zoomBy(1 / 1.25));
    zout.title = "Zoom out";
    const zreset = this.makeButton("⟲", () => {
      this.resetView();
      this.update();
    });
    zreset.title = "Reset view (fit)";

    zoomBox.appendChild(zin);
    zoomBox.appendChild(zout);
    zoomBox.appendChild(zreset);

    ui.appendChild(topRight);
    ui.appendChild(zoomBox);
    return ui;
  }

  private makeButton(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.textContent = label;
    Object.assign(b.style, {
      minWidth: "22px",
      height: "22px",
      padding: "0",
      lineHeight: "20px",
      fontSize: "13px",
      fontFamily: "ui-monospace, monospace",
      color: "rgba(200,218,235,0.92)",
      background: "rgba(20,26,34,0.82)",
      border: "1px solid rgba(120,140,165,0.35)",
      borderRadius: "4px",
      cursor: "pointer",
      userSelect: "none",
    } as Partial<CSSStyleDeclaration>);
    // Buttons must NOT bubble up to the canvas/background expand toggle.
    b.addEventListener("pointerdown", (e) => e.stopPropagation());
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      onClick();
    });
    b.addEventListener("mouseenter", () => {
      b.style.background = "rgba(38,48,62,0.92)";
    });
    b.addEventListener("mouseleave", () => {
      b.style.background = "rgba(20,26,34,0.82)";
    });
    return b;
  }

  private cycleProjection(): void {
    const idx = PROJECTIONS.indexOf(this.projection);
    const next = PROJECTIONS[(idx + 1) % PROJECTIONS.length] ?? "front";
    this.setProjection(next);
  }

  private syncControls(): void {
    if (this.projLabelBtn) this.projLabelBtn.textContent = `View: ${this.projection.toUpperCase()}`;
    if (this.expandBtn) {
      this.expandBtn.textContent = this.expanded ? "✕" : "⤢";
      this.expandBtn.style.display = this.mainMode ? "none" : "";
    }
  }

  // --- projection helpers ------------------------------------------------

  /** Horizontal world coordinate for a node under the current projection. */
  private hOf(x: number, z: number): number {
    // front -> x, side -> z, top(plan) -> x
    return this.projection === "side" ? z : x;
  }

  /** Vertical (screen-up) world coordinate for a node under the projection. */
  private vOf(y: number, z: number): number {
    // front/side -> world y (height); top(plan) -> world z (depth into page)
    return this.projection === "top" ? z : y;
  }

  /** Apply auto-fit then the interactive view transform (device pixels). */
  private projectH(h: number): number {
    const fit = this.offH + h * this.scale;
    const cx = this.canvas.width / 2;
    return (fit - cx) * this.zoom + cx + this.panX;
  }

  private projectV(v: number): number {
    // Invert: world up -> canvas y-down, then apply the view transform.
    const fit = this.offV - v * this.scale;
    const cy = this.canvas.height / 2;
    return (fit - cy) * this.zoom + cy + this.panY;
  }

  private unprojectH(px: number): number {
    const cx = this.canvas.width / 2;
    const fit = (px - cx - this.panX) / this.zoom + cx;
    return (fit - this.offH) / this.scale;
  }

  private unprojectV(py: number): number {
    const cy = this.canvas.height / 2;
    const fit = (py - cy - this.panY) / this.zoom + cy;
    return (this.offV - fit) / this.scale;
  }

  private worldFromClient(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!this.bounds) return null;
    const rect = this.canvas.getBoundingClientRect();
    const px = (clientX - rect.left) * this.dpr;
    const py = (clientY - rect.top) * this.dpr;
    return {
      x: this.unprojectH(px),
      y: Math.max(0, this.unprojectV(py)),
    };
  }

  private pickMemberAt(clientX: number, clientY: number): number | null {
    const s = this.structure;
    if (!s) return null;
    const rect = this.canvas.getBoundingClientRect();
    const px = (clientX - rect.left) * this.dpr;
    const py = (clientY - rect.top) * this.dpr;
    const tol = 12 * this.dpr;
    let bestIdx: number | null = null;
    let bestD = tol;
    for (let i = 0; i < s.members.length; i++) {
      const mem = s.members[i]!;
      const a = s.nodes[mem.i];
      const b = s.nodes[mem.j];
      if (!a || !b) continue;
      const x1 = this.projectH(this.hOf(a.x, a.z));
      const y1 = this.projectV(this.vOf(a.y, a.z));
      const x2 = this.projectH(this.hOf(b.x, b.z));
      const y2 = this.projectV(this.vOf(b.y, b.z));
      const d = pointSegmentDistance(px, py, x1, y1, x2, y2);
      if (d < bestD) {
        bestD = d;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  private computeBounds(structure: Structure, meta: FitMeta): Bounds {
    const isTop = this.projection === "top";
    let minH = Infinity;
    let maxH = -Infinity;
    // Front/side include the ground line y = 0; plan has no ground datum.
    let minV = isTop ? Infinity : 0;
    let maxV = -Infinity;

    for (const n of structure.nodes) {
      const h = this.hOf(n.x, n.z);
      const v = this.vOf(n.y, n.z);
      if (h < minH) minH = h;
      if (h > maxH) maxH = h;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }

    // Prefer explicit meta if provided (guy-anchor spans can exceed node reach
    // in some layouts); fall back to node-derived bounds otherwise.
    if (isTop) {
      // Plan view: both axes are horizontal footprint. Use halfWidth on both.
      if (meta.halfWidth !== undefined) {
        minH = Math.min(minH, -meta.halfWidth);
        maxH = Math.max(maxH, meta.halfWidth);
        minV = Math.min(minV, -meta.halfWidth);
        maxV = Math.max(maxV, meta.halfWidth);
      }
    } else {
      if (meta.height !== undefined) maxV = Math.max(maxV, meta.height);
      if (meta.halfWidth !== undefined) {
        minH = Math.min(minH, -meta.halfWidth);
        maxH = Math.max(maxH, meta.halfWidth);
      }
    }

    // Degenerate guard: a single point or empty structure.
    if (!isFinite(minH) || !isFinite(maxH)) {
      minH = -1;
      maxH = 1;
    }
    if (!isFinite(minV) || !isFinite(maxV)) {
      minV = isTop ? -1 : 0;
      maxV = 1;
    }
    if (maxH - minH < 1e-6) {
      minH -= 1;
      maxH += 1;
    }
    if (maxV - minV < 1e-6) maxV = minV + 1;

    // Minimum world window so a blank/tiny draw canvas stays at a usable tower
    // scale — otherwise the auto-fit zooms into a ~1 m window and freehand strokes
    // map to centimetres. Real towers (≥ ~15 m) exceed these floors, so viewing is
    // unaffected; this only rescues the empty/just-started free-draw case. Front and
    // side keep minV = 0, so the ground stays pinned at the bottom of the view.
    const MIN_SPAN_V = 15; // metres of vertical working height
    const MIN_SPAN_H = 10; // metres of horizontal working width
    const growCentered = (lo: number, hi: number, span: number): [number, number] => {
      const c = (lo + hi) / 2;
      const half = Math.max((hi - lo) / 2, span / 2);
      return [c - half, c + half];
    };
    if (isTop) {
      [minH, maxH] = growCentered(minH, maxH, MIN_SPAN_H);
      [minV, maxV] = growCentered(minV, maxV, MIN_SPAN_H);
    } else {
      if (maxV - minV < MIN_SPAN_V) maxV = minV + MIN_SPAN_V; // grow upward from ground
      [minH, maxH] = growCentered(minH, maxH, MIN_SPAN_H);
    }

    return { minH, maxH, minV, maxV };
  }

  /** Recompute scale + offsets so the undeformed bounds fit with a margin. */
  private recomputeFit(): void {
    const b = this.bounds;
    const W = this.canvas.width;
    const H = this.canvas.height;
    if (!b || W === 0 || H === 0) return;

    const m = MARGIN_PX * this.dpr;
    const sideGutter = RULER_SIDE_GUTTER_PX * this.dpr;
    const topGutter = RULER_TOP_GUTTER_PX * this.dpr;
    const bottomGutter = RULER_BOTTOM_GUTTER_PX * this.dpr;

    const availW = Math.max(1, W - m * 2 - sideGutter * 2);
    const availH = Math.max(1, H - m * 2 - topGutter - bottomGutter);

    const worldW = b.maxH - b.minH;
    const worldH = b.maxV - b.minV;

    // Single uniform scale keeps the drawing to true proportions (no distortion).
    this.scale = Math.min(availW / worldW, availH / worldH) * FIT_LOOSEN_FACTOR;

    // Centre horizontally within the drawing area (offset past the rulers).
    const drawnW = worldW * this.scale;
    const left = m + sideGutter + (availW - drawnW) / 2;
    this.offH = left - b.minH * this.scale;

    // Vertical: pin the top of the fit near the top margin; projectV inverts.
    // offV is the device-y that maps to world v = 0 (ground).
    const drawnH = worldH * this.scale;
    const top = m + topGutter + (availH - drawnH) / 2;
    const bottom = top + drawnH;
    // world minV maps to `bottom`, so: bottom = offV - minV*scale
    this.offV = bottom + b.minV * this.scale;
  }

  // --- drawing -----------------------------------------------------------

  private drawGridAndTicks(): void {
    const b = this.bounds;
    if (!b) return;
    const { ctx } = this;
    const W = this.canvas.width;
    const H = this.canvas.height;
    const vStep = pickTickStep(b.maxV - b.minV, 7);
    const hStep = pickTickStep(b.maxH - b.minH, 8);
    const leftX = 6 * this.dpr;
    const rightX = W - 6 * this.dpr;
    const topY = 6 * this.dpr;
    const bottomY = H - 8 * this.dpr;

    ctx.lineWidth = Math.max(1, this.dpr);
    ctx.font = `${Math.round(11 * this.dpr)}px ui-monospace, monospace`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";

    // Vertical world-axis scale on both sides.
    const vStart = Math.ceil(b.minV / vStep) * vStep;
    for (let yM = vStart; yM <= b.maxV + 1e-6; yM += vStep) {
      const py = Math.round(this.projectV(yM)) + 0.5;
      if (py < 0 || py > H) continue;
      ctx.strokeStyle = GRID;
      ctx.beginPath();
      ctx.moveTo(0, py);
      ctx.lineTo(W, py);
      ctx.stroke();

      ctx.fillStyle = TICK_TEXT;
      ctx.textAlign = "left";
      ctx.fillText(`${trimTick(yM)} m`, leftX, py);
      ctx.textAlign = "right";
      ctx.fillText(`${trimTick(yM)} m`, rightX, py);
    }

    // Horizontal world-axis scale on both top and bottom edges.
    const hStart = Math.ceil(b.minH / hStep) * hStep;
    ctx.textBaseline = "top";
    for (let xM = hStart; xM <= b.maxH + 1e-6; xM += hStep) {
      const px = Math.round(this.projectH(xM)) + 0.5;
      if (px < 0 || px > W) continue;
      ctx.strokeStyle = GRID;
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px, H);
      ctx.stroke();

      ctx.fillStyle = TICK_TEXT;
      ctx.textAlign = "center";
      ctx.fillText(`${trimTick(xM)} m`, px, topY);
      ctx.textBaseline = "bottom";
      ctx.fillText(`${trimTick(xM)} m`, px, bottomY);
      ctx.textBaseline = "top";
    }
  }

  /** Draw the concrete footing(s) below the ground line so the buried depth is
   *  visible in the elevation — the concrete pad the 3D view only shows at grade. */
  private drawFoundation(): void {
    if (this.projection === "top") return;
    const f = this.meta.foundation;
    const s = this.structure;
    if (!f || !s) return;
    const { ctx } = this;
    const base = f.base;
    const isPier = base.type === "pier" || base.type === "rock-anchor";
    const wpx = Math.max(4, (isPier ? base.diameter : base.width) * this.scale);
    const dpx = Math.max(5, base.depth * this.scale);
    const groundY = this.projectV(0);
    const nearBase = (this.meta.halfWidth ?? 3) * 1.6 + 1; // exclude far guy anchors

    ctx.save();
    ctx.fillStyle = "rgba(196, 190, 178, 0.5)"; // concrete, translucent (it's buried)
    ctx.strokeStyle = "rgba(216, 210, 198, 0.9)";
    ctx.lineWidth = Math.max(1, this.dpr);
    const seen = new Set<number>();
    for (const n of s.nodes) {
      if (!n.fixed) continue;
      const h = this.hOf(n.x, n.z);
      if (Math.abs(h) > nearBase) continue;
      const hx = Math.round(this.projectH(h));
      const key = Math.round(hx / 3);
      if (seen.has(key)) continue;
      seen.add(key);
      ctx.fillRect(hx - wpx / 2, groundY, wpx, dpx);
      ctx.strokeRect(hx - wpx / 2 + 0.5, groundY + 0.5, wpx, dpx);
    }
    if (seen.size) {
      ctx.fillStyle = "rgba(200, 210, 220, 0.75)";
      ctx.font = `${Math.round(10 * this.dpr)}px ui-monospace, monospace`;
      ctx.textBaseline = "top";
      ctx.textAlign = "left";
      const kind = isPier ? "pier" : "pad";
      ctx.fillText(`${kind} ${base.depth.toFixed(1)} m deep`, 4 * this.dpr, groundY + dpx + 3 * this.dpr);
    }
    ctx.restore();
  }

  private drawGround(): void {
    // Plan (top) view looks straight down — there is no ground line to draw.
    if (this.projection === "top") return;

    const { ctx } = this;
    const W = this.canvas.width;
    const py = Math.round(this.projectV(0)) + 0.5;

    ctx.strokeStyle = GROUND;
    ctx.lineWidth = Math.max(1.5, 1.5 * this.dpr);
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.moveTo(0, py);
    ctx.lineTo(W, py);
    ctx.stroke();

    // Subtle hatch below the ground line to read as "earth".
    ctx.strokeStyle = "rgba(150, 170, 190, 0.18)";
    ctx.lineWidth = Math.max(1, this.dpr);
    const step = 14 * this.dpr;
    const depth = 10 * this.dpr;
    for (let x = 0; x < W + depth; x += step) {
      ctx.beginPath();
      ctx.moveTo(x, py);
      ctx.lineTo(x - depth, py + depth);
      ctx.stroke();
    }
  }

  private drawMembers(s: Structure): void {
    const { ctx } = this;
    const nodes = s.nodes;

    ctx.lineCap = "round";

    for (let idx = 0; idx < s.members.length; idx++) {
      const mem = s.members[idx]!;
      const a = nodes[mem.i];
      const bNode = nodes[mem.j];
      if (!a || !bNode) continue;

      const x1 = this.projectH(this.hOf(a.x, a.z));
      const y1 = this.projectV(this.vOf(a.y, a.z));
      const x2 = this.projectH(this.hOf(bNode.x, bNode.z));
      const y2 = this.projectV(this.vOf(bNode.y, bNode.z));

      if (mem.failed) {
        // Broken member: short dark-red dashed stub with an ✕ at its midpoint.
        ctx.strokeStyle = FAILED;
        ctx.lineWidth = Math.max(1, this.dpr);
        ctx.setLineDash([3 * this.dpr, 3 * this.dpr]);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.setLineDash([]);
        this.drawCross((x1 + x2) / 2, (y1 + y2) / 2);
        continue;
      }

      const type = classifySection(mem);
      const style = SECTION_STYLES[type];

      if (mem.tensionOnly && mem.slack) {
        // Slack guy: thin, muted, dashed — carrying no load. Keep the guy dash
        // signature so it still reads as a cable, just greyed out.
        ctx.lineCap = "round";
        ctx.strokeStyle = SLACK_GUY;
        ctx.lineWidth = Math.max(1, 1 * this.dpr);
        ctx.setLineDash([4 * this.dpr, 4 * this.dpr]);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.setLineDash([]);
        continue;
      }

      // Live member: colour by utilization (green→red); the SECTION TYPE is
      // conveyed by stroke STYLE (line cap + dash + edge), and the line WIDTH is
      // driven by the exaggerated section-size mapping. Width also tracks zoom
      // (and the auto-fit scale) so the emphasis holds at pip and fullscreen.
      const sim = this.isSimulating();
      const zoomW = Math.min(Math.max(this.zoom, 0.6), 2.6);
      // dpr keeps it crisp on hi-DPI; a gentle scale-tie widens lines when the
      // whole drawing is large (expanded view) and thins them at pip size.
      const fitW = Math.min(Math.max(this.scale / 6, 0.55), 2.0);
      let baseW = sectionBaseWidthPx(mem.section.dragWidth, type);
      // A touch of utilization emphasis so hot members also read heavier — but only
      // while simulating, so the drawing stays stable as you design.
      if (sim) baseW += mem.util * 0.8;
      const lw = Math.max(1, baseW * this.dpr * zoomW * fitW);

      // Colour by SECTION while designing (stable, matches the palette + key);
      // colour by utilization (green→red) only while simulating.
      // Colour by MATERIAL while designing (section type is shown by stroke style);
      // colour by utilization (green→red) while simulating.
      ctx.strokeStyle = sim ? utilColor(mem.util) : materialColor(mem.material.name);
      ctx.lineCap = style.cap;
      ctx.setLineDash(style.dash.map((d) => d * this.dpr));
      ctx.lineWidth = lw;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Angle (L): overlay a thin parallel "edge" stroke offset to one side of
      // the member so it reads as an L / two-faced profile, not a plain bar.
      if (style.edge) {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const len = Math.hypot(dx, dy);
        if (len > 1e-3) {
          // Unit normal, offset by ~a third of the member width.
          const off = (lw * 0.42);
          const nx = (-dy / len) * off;
          const ny = (dx / len) * off;
          ctx.lineCap = "butt";
          ctx.setLineDash([]);
          ctx.lineWidth = Math.max(1, this.dpr);
          ctx.beginPath();
          ctx.moveTo(x1 + nx, y1 + ny);
          ctx.lineTo(x2 + nx, y2 + ny);
          ctx.stroke();
        }
      }

      if (idx === this.selectedMemberIdx) {
        ctx.strokeStyle = "rgba(102, 224, 255, 0.92)";
        ctx.lineCap = "round";
        ctx.setLineDash([]);
        ctx.lineWidth = Math.max(lw + 5 * this.dpr, 7 * this.dpr);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    }
    ctx.setLineDash([]);
    ctx.lineCap = "round";
  }

  /** Snap-grid dots (1 m) across the visible area, ground up — front-view editing only. */
  private drawSnapGrid(): void {
    if (!this.canEdit()) return;
    const { ctx } = this;
    const W = this.canvas.width;
    const H = this.canvas.height;
    const loX = this.unprojectH(0);
    const hiX = this.unprojectH(W);
    const topY = Math.max(0, this.unprojectV(0)); // world height at the top edge
    const x0 = Math.ceil(Math.min(loX, hiX) / SNAP_STEP_M) * SNAP_STEP_M;
    const cols = Math.floor((Math.max(loX, hiX) - x0) / SNAP_STEP_M) + 1;
    const rows = Math.floor(topY / SNAP_STEP_M) + 1;
    if (cols <= 0 || rows <= 0 || cols * rows > 4000) return; // don't carpet a zoomed-out view
    ctx.save();
    ctx.fillStyle = SNAP_DOT;
    const r = Math.max(1, 1.1 * this.dpr);
    for (let xi = 0; xi < cols; xi++) {
      const sx = this.projectH(x0 + xi * SNAP_STEP_M);
      if (sx < 0 || sx > W) continue;
      for (let yi = 0; yi < rows; yi++) {
        const sy = this.projectV(yi * SNAP_STEP_M);
        if (sy < 0 || sy > H) continue;
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }

  /** Cursor crosshair, snapped-coordinate readout, and nearest-node highlight. */
  private drawCursorReadout(): void {
    if (!this.canEdit() || !this.hoverWorld) return;
    const { ctx } = this;
    const W = this.canvas.width;
    const hx = this.hoverWorld.x;
    const hy = this.hoverWorld.y;
    // Snap to the grid, then to an existing node if the cursor is near one.
    let sx = Math.round(hx / SNAP_STEP_M) * SNAP_STEP_M;
    let sy = Math.max(0, Math.round(hy / SNAP_STEP_M) * SNAP_STEP_M);
    let onNode = false;
    const overlay = this.getEditOverlay();
    if (overlay) {
      let best = 0.6; // metres
      for (const n of overlay.nodes) {
        const d = Math.hypot(n.x - hx, n.y - hy);
        if (d < best) {
          best = d;
          sx = n.x;
          sy = n.y;
          onNode = true;
        }
      }
    }
    const px = this.projectH(sx);
    const py = this.projectV(sy);
    ctx.save();
    ctx.setLineDash([]);
    ctx.strokeStyle = DRAW_ACCENT;
    ctx.lineWidth = Math.max(1, this.dpr);
    const c = 8 * this.dpr;
    ctx.beginPath();
    ctx.moveTo(px - c, py);
    ctx.lineTo(px + c, py);
    ctx.moveTo(px, py - c);
    ctx.lineTo(px, py + c);
    ctx.stroke();
    if (onNode) {
      ctx.beginPath();
      ctx.arc(px, py, 6 * this.dpr, 0, Math.PI * 2);
      ctx.stroke();
    }
    const label = `${sx.toFixed(1)}, ${sy.toFixed(1)} m`;
    ctx.font = `${Math.round(11 * this.dpr)}px ui-monospace, monospace`;
    const tw = ctx.measureText(label).width;
    const lx = Math.min(px + 12 * this.dpr, W - tw - 8 * this.dpr);
    const ly = Math.max(py - 22 * this.dpr, 4 * this.dpr);
    ctx.fillStyle = "rgba(8, 12, 18, 0.85)";
    ctx.fillRect(lx - 4 * this.dpr, ly - 2 * this.dpr, tw + 8 * this.dpr, 16 * this.dpr);
    ctx.fillStyle = DRAW_ACCENT;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(label, lx, ly);
    ctx.restore();
  }

  /** Pick the authored (overlay) member nearest the cursor — what the user sees/clicks. */
  private pickOverlayMemberAt(clientX: number, clientY: number): SchematicEditOverlay["members"][number] | null {
    const overlay = this.getEditOverlay();
    if (!overlay) return null;
    const rect = this.canvas.getBoundingClientRect();
    const px = (clientX - rect.left) * this.dpr;
    const py = (clientY - rect.top) * this.dpr;
    const nodes = new Map(overlay.nodes.map((n) => [n.id, n]));
    const tol = 14 * this.dpr;
    let best: SchematicEditOverlay["members"][number] | null = null;
    let bestD = tol;
    for (const m of overlay.members) {
      const a = nodes.get(m.a);
      const b = nodes.get(m.b);
      if (!a || !b) continue;
      const d = pointSegmentDistance(
        px,
        py,
        this.projectH(a.x),
        this.projectV(a.y),
        this.projectH(b.x),
        this.projectV(b.y),
      );
      if (d < bestD) {
        bestD = d;
        best = m;
      }
    }
    return best;
  }

  /** On click, gather a material-info tip for the member under the cursor (or clear). */
  private showMemberInfoAt(clientX: number, clientY: number): void {
    const m = this.pickOverlayMemberAt(clientX, clientY);
    if (!m) {
      this.infoTip = null;
      return;
    }
    const key = (m.material ?? "a572") as keyof typeof MATERIALS;
    const mat = MATERIALS[key];
    const rect = this.canvas.getBoundingClientRect();
    this.infoTip = {
      sx: (clientX - rect.left) * this.dpr,
      sy: (clientY - rect.top) * this.dpr,
      color: materialColorByKey(key),
      lines: [
        mat.name,
        `Fy ${Math.round(mat.Fy / 1e6)} MPa · E ${Math.round(mat.E / 1e9)} GPa · ρ ${mat.rho} kg/m³`,
        `section: ${m.section}`,
      ],
    };
  }

  /** A small material-info popup at the clicked member (design-mode analogue of the 3D inspector). */
  private drawInfoTip(): void {
    if (!this.infoTip) return;
    const t = this.infoTip;
    const { ctx } = this;
    ctx.save();
    ctx.font = `${Math.round(11 * this.dpr)}px ui-monospace, monospace`;
    const w = Math.max(...t.lines.map((l) => ctx.measureText(l).width)) + 26 * this.dpr;
    const lh = 15 * this.dpr;
    const h = t.lines.length * lh + 8 * this.dpr;
    let bx = Math.min(t.sx + 14 * this.dpr, this.canvas.width - w - 4 * this.dpr);
    let by = Math.min(t.sy + 10 * this.dpr, this.canvas.height - h - 4 * this.dpr);
    bx = Math.max(bx, 2 * this.dpr);
    by = Math.max(by, 2 * this.dpr);
    ctx.fillStyle = "rgba(10, 14, 20, 0.93)";
    ctx.strokeStyle = t.color;
    ctx.lineWidth = Math.max(1, this.dpr);
    ctx.fillRect(bx, by, w, h);
    ctx.strokeRect(bx + 0.5, by + 0.5, w, h);
    ctx.fillStyle = t.color;
    ctx.fillRect(bx + 6 * this.dpr, by + 6 * this.dpr, 9 * this.dpr, 9 * this.dpr);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    t.lines.forEach((l, i) => {
      ctx.fillStyle = i === 0 ? t.color : "rgba(200,214,228,0.9)";
      ctx.fillText(l, i === 0 ? bx + 20 * this.dpr : bx + 8 * this.dpr, by + 5 * this.dpr + i * lh);
    });
    ctx.restore();
  }

  /** The floating copy-paste part: cyan dashed ghost + scale/angle readout + hints. */
  private drawPlacementGhost(): void {
    if (!this.isPlacing()) return;
    const g = this.getEditOverlay()!.placement!;
    const { ctx } = this;
    const W = this.canvas.width;
    ctx.save();
    ctx.lineCap = "round";
    ctx.setLineDash([6 * this.dpr, 5 * this.dpr]);
    ctx.strokeStyle = DRAW_ACCENT;
    ctx.lineWidth = Math.max(2, 2.5 * this.dpr);
    for (const m of g.members) {
      ctx.beginPath();
      ctx.moveTo(this.projectH(m.x1), this.projectV(m.y1));
      ctx.lineTo(this.projectH(m.x2), this.projectV(m.y2));
      ctx.stroke();
    }
    ctx.setLineDash([]);
    ctx.fillStyle = DRAW_ACCENT;
    for (const n of g.nodes) {
      ctx.beginPath();
      ctx.arc(this.projectH(n.x), this.projectV(n.y), 3 * this.dpr, 0, Math.PI * 2);
      ctx.fill();
    }
    // Top-centre banner: current transform + controls.
    const line1 = `PASTING  ${g.scale.toFixed(2)}×  ${g.angleDeg}°`;
    const line2 = "click to place · wheel scale · middle-click / R rotate · Esc cancel";
    ctx.font = `${Math.round(12 * this.dpr)}px ui-monospace, monospace`;
    const w1 = ctx.measureText(line1).width;
    ctx.font = `${Math.round(10.5 * this.dpr)}px ui-monospace, monospace`;
    const w2 = ctx.measureText(line2).width;
    const bw = Math.max(w1, w2) + 16 * this.dpr;
    const bx = (W - bw) / 2;
    const by = 8 * this.dpr;
    ctx.fillStyle = "rgba(8, 12, 18, 0.88)";
    ctx.fillRect(bx, by, bw, 34 * this.dpr);
    ctx.strokeStyle = DRAW_ACCENT;
    ctx.lineWidth = Math.max(1, this.dpr);
    ctx.strokeRect(bx + 0.5, by + 0.5, bw, 34 * this.dpr);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = DRAW_ACCENT;
    ctx.font = `${Math.round(12 * this.dpr)}px ui-monospace, monospace`;
    ctx.fillText(line1, W / 2, by + 4 * this.dpr);
    ctx.fillStyle = "rgba(180, 200, 218, 0.9)";
    ctx.font = `${Math.round(10.5 * this.dpr)}px ui-monospace, monospace`;
    ctx.fillText(line2, W / 2, by + 20 * this.dpr);
    ctx.restore();
  }

  private drawEditOverlay(): void {
    if (!this.isDrawEnabled()) return;
    // The authored edit graph lives in the X–Y (front-elevation) plane, so it only
    // makes sense in the front projection. Drawing it in side/top projected height
    // onto the z-axis, painting phantom lines that aren't real members. Editing is
    // a front-elevation activity, so hide the overlay elsewhere; the real extruded
    // members still render in every view via drawMembers(). Prompt the user back.
    if (this.projection !== "front") {
      const { ctx } = this;
      ctx.save();
      ctx.font = `${Math.round(12 * this.dpr)}px ui-monospace, monospace`;
      ctx.fillStyle = "rgba(255, 198, 112, 0.92)";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText("Switch to FRONT view to edit the drawing", this.canvas.width / 2, 12 * this.dpr);
      ctx.restore();
      return;
    }
    const overlay = this.getEditOverlay();
    if (!overlay) return;
    const { ctx } = this;
    const nodes = new Map(overlay.nodes.map((n) => [n.id, n]));

    ctx.save();
    if (overlay.mirror?.enabled) {
      const x = Math.round(this.projectH(overlay.mirror.axisX)) + 0.5;
      const yTop = 0;
      const yBottom = this.canvas.height;
      // Draw a two-pass amber axis so it never blends with the cyan draw preview.
      ctx.strokeStyle = "rgba(10, 14, 18, 0.9)";
      ctx.lineWidth = Math.max(4, 4 * this.dpr);
      ctx.setLineDash([2 * this.dpr, 5 * this.dpr]);
      ctx.beginPath();
      ctx.moveTo(x, yTop);
      ctx.lineTo(x, yBottom);
      ctx.stroke();

      ctx.strokeStyle = "rgba(255, 184, 77, 0.98)";
      ctx.lineWidth = Math.max(2, 2 * this.dpr);
      ctx.setLineDash([10 * this.dpr, 6 * this.dpr]);
      ctx.beginPath();
      ctx.moveTo(x, yTop);
      ctx.lineTo(x, yBottom);
      ctx.stroke();

      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(255, 198, 112, 0.98)";
      ctx.font = `${Math.round(11 * this.dpr)}px ui-monospace, monospace`;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("MIRROR AXIS", x + 6 * this.dpr, 8 * this.dpr);
    }

    const drawOverlayMember = (m: SchematicEditOverlay["members"][number], selected: boolean) => {
      const a = nodes.get(m.a);
      const b = nodes.get(m.b);
      if (!a || !b) return;
      const x1 = this.projectH(a.x);
      const y1 = this.projectV(a.y);
      const x2 = this.projectH(b.x);
      const y2 = this.projectV(b.y);
      const base = m.material ? materialColorByKey(m.material) : overlaySectionColor(m.section);
      ctx.lineCap = "round";
      ctx.setLineDash([]);
      if (selected) {
        ctx.strokeStyle = "rgba(8, 12, 18, 0.95)";
        ctx.lineWidth = Math.max(10, 10 * this.dpr);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.strokeStyle = base;
        ctx.lineWidth = Math.max(6, 6 * this.dpr);
      } else {
        ctx.strokeStyle = base;
        ctx.lineWidth = Math.max(2.5, 2.5 * this.dpr);
      }
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    };
    for (const m of overlay.members) {
      if (!m.selected) drawOverlayMember(m, false);
    }
    for (const m of overlay.members) {
      if (m.selected) drawOverlayMember(m, true);
    }

    for (const n of overlay.nodes) {
      const x = this.projectH(n.x);
      const y = this.projectV(n.y);
      const r = (n.selected ? 6 : 3.5) * this.dpr;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = n.selected ? "rgba(255, 230, 92, 0.98)" : n.fixed ? "rgba(245, 167, 66, 0.95)" : "rgba(255, 255, 255, 0.96)";
      ctx.fill();
      ctx.lineWidth = n.selected ? Math.max(3, 3 * this.dpr) : Math.max(1, this.dpr);
      ctx.strokeStyle = "rgba(8, 12, 18, 0.95)";
      ctx.stroke();
    }
    ctx.restore();
  }

  /**
   * Snap the drawn line to a "good" angle — horizontal, vertical, 45°, or PARALLEL
   * to an existing member — when the raw drag is within a few degrees, keeping the
   * cursor's distance. Returns the snapped end + a hint label (or null = free).
   */
  private snapDrawEnd(
    start: { x: number; y: number },
    cur: { x: number; y: number },
  ): { end: { x: number; y: number }; hint: string | null } {
    const dx = cur.x - start.x;
    const dy = cur.y - start.y;
    const len = Math.hypot(dx, dy);
    if (len < 0.3) return { end: cur, hint: null };
    const raw = Math.atan2(dy, dx);
    const cands: Array<{ a: number; hint: string }> = [];
    const axis = ["horizontal", "45°", "vertical", "45°"];
    for (let k = 0; k < 8; k++) cands.push({ a: (k * Math.PI) / 4, hint: axis[k % 4]! });
    const overlay = this.getEditOverlay();
    if (overlay) {
      const nodes = new Map(overlay.nodes.map((n) => [n.id, n]));
      for (const m of overlay.members) {
        const a = nodes.get(m.a);
        const b = nodes.get(m.b);
        if (!a || !b) continue;
        const ang = Math.atan2(b.y - a.y, b.x - a.x);
        cands.push({ a: ang, hint: "parallel" }, { a: ang + Math.PI, hint: "parallel" });
      }
    }
    const tol = (6 * Math.PI) / 180;
    let bestA = raw;
    let bestHint: string | null = null;
    let bestDiff = tol;
    for (const c of cands) {
      const d = Math.abs(Math.atan2(Math.sin(raw - c.a), Math.cos(raw - c.a)));
      if (d < bestDiff) {
        bestDiff = d;
        bestA = c.a;
        bestHint = c.hint;
      }
    }
    return {
      end: { x: start.x + Math.cos(bestA) * len, y: Math.max(0, start.y + Math.sin(bestA) * len) },
      hint: bestHint,
    };
  }

  private drawMemberPreview(): void {
    if (!this.drawStart || !this.drawCurrent) return;
    const { ctx } = this;
    const end = this.editGesture === "draw" && this.drawSnapEnd ? this.drawSnapEnd : this.drawCurrent;
    const x1 = this.projectH(this.drawStart.x);
    const y1 = this.projectV(this.drawStart.y);
    const x2 = this.projectH(end.x);
    const y2 = this.projectV(end.y);
    ctx.save();
    if (this.editGesture === "select") {
      const x = Math.min(x1, x2);
      const y = Math.min(y1, y2);
      const w = Math.abs(x2 - x1);
      const h = Math.abs(y2 - y1);
      ctx.fillStyle = "rgba(125, 211, 252, 0.10)";
      ctx.strokeStyle = "rgba(125, 211, 252, 0.85)";
      ctx.lineWidth = Math.max(1, this.dpr);
      ctx.setLineDash([5 * this.dpr, 4 * this.dpr]);
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
    } else {
      const snapped = this.editGesture === "draw" && this.drawSnapHint;
      ctx.strokeStyle = snapped ? "rgba(255, 198, 112, 0.98)" : "rgba(125, 211, 252, 0.95)";
      ctx.lineWidth = Math.max(2, 2 * this.dpr);
      ctx.setLineDash([6 * this.dpr, 4 * this.dpr]);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      // Snap hint label near the moving end.
      if (snapped && this.drawSnapHint) {
        ctx.setLineDash([]);
        ctx.font = `${Math.round(11 * this.dpr)}px ui-monospace, monospace`;
        ctx.fillStyle = "rgba(255, 198, 112, 0.98)";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText(this.drawSnapHint, x2 + 8 * this.dpr, y2 - 8 * this.dpr);
      }
    }
    ctx.restore();
  }

  private drawCross(cx: number, cy: number): void {
    const { ctx } = this;
    const r = 3 * this.dpr;
    ctx.strokeStyle = FAILED;
    ctx.lineWidth = Math.max(1, this.dpr);
    ctx.beginPath();
    ctx.moveTo(cx - r, cy - r);
    ctx.lineTo(cx + r, cy + r);
    ctx.moveTo(cx + r, cy - r);
    ctx.lineTo(cx - r, cy + r);
    ctx.stroke();
  }

  private drawTitle(): void {
    const { ctx } = this;
    const pad = 8 * this.dpr;
    ctx.fillStyle = TITLE_TEXT;
    ctx.font = `${Math.round(12 * this.dpr)}px ui-monospace, monospace`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    let label: string;
    switch (this.projection) {
      case "front":
        label = "ELEVATION — FRONT (X–Y)";
        break;
      case "side":
        label = "SECTION — SIDE (Z–Y)";
        break;
      case "top":
        label = "PLAN — TOP (X–Z)";
        break;
    }
    ctx.fillText(label, pad, pad);
  }

  private drawLegend(): void {
    // The utilization ramp only means something while simulating; in design mode
    // members are coloured by section, so hide it to avoid implying stress.
    if (!this.isSimulating()) return;
    const { ctx } = this;
    const H = this.canvas.height;
    const W = this.canvas.width;

    const barW = 90 * this.dpr;
    const barH = 7 * this.dpr;
    // Leave room on the right for the zoom buttons overlay (~34px wide).
    const x0 = W - barW - 44 * this.dpr;
    const y0 = H - barH - 18 * this.dpr;

    // Colour ramp swatch (sampled so it matches utilColor exactly).
    const steps = 24;
    for (let i = 0; i < steps; i++) {
      const u = i / (steps - 1);
      ctx.fillStyle = utilColor(u);
      const sx = x0 + (i / steps) * barW;
      ctx.fillRect(Math.floor(sx), y0, Math.ceil(barW / steps) + 1, barH);
    }

    ctx.strokeStyle = "rgba(200,218,235,0.3)";
    ctx.lineWidth = Math.max(1, this.dpr);
    ctx.strokeRect(x0 + 0.5, y0 + 0.5, barW, barH);

    ctx.fillStyle = TICK_TEXT;
    ctx.font = `${Math.round(10 * this.dpr)}px ui-monospace, monospace`;
    ctx.textBaseline = "top";
    ctx.textAlign = "left";
    ctx.fillText("0", x0, y0 + barH + 2 * this.dpr);
    ctx.textAlign = "right";
    ctx.fillText("util 1.0", x0 + barW, y0 + barH + 2 * this.dpr);

    // Caption above the bar.
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillStyle = TITLE_TEXT;
    ctx.fillText("UTILIZATION", x0, y0 - 2 * this.dpr);
  }

  /**
   * Section-type key: a small stack of sample strokes (drawn with the same
   * per-type style used for members) mapping stroke STYLE → section family.
   * Colour here is neutral (type is conveyed by style, not colour) and the
   * sample thickness hints at the light→heavy exaggeration. Sits bottom-left,
   * clear of the title (top) and the utilization ramp (bottom-right). Shows a
   * richer variant when the panel is expanded.
   */
  /** A colour key for the DISTINCT materials in the tower — shown only when mixed. */
  private drawMaterialLegend(): void {
    if (this.isSimulating() || !this.structure) return;
    const names: string[] = [];
    const seen = new Set<string>();
    for (const m of this.structure.members) {
      if (!seen.has(m.material.name)) {
        seen.add(m.material.name);
        names.push(m.material.name);
      }
    }
    if (names.length <= 1) return; // a key is only useful with a mix
    const { ctx } = this;
    const x0 = (8 + 26) * this.dpr;
    let y = 44 * this.dpr; // clear of the top title line
    ctx.save();
    ctx.font = `${Math.round(10 * this.dpr)}px ui-monospace, monospace`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillStyle = TITLE_TEXT;
    ctx.fillText("MATERIAL", x0, y);
    y += 14 * this.dpr;
    for (const name of names) {
      ctx.fillStyle = materialColor(name);
      ctx.fillRect(x0, y - 4 * this.dpr, 11 * this.dpr, 8 * this.dpr);
      ctx.fillStyle = TICK_TEXT;
      ctx.fillText(name, x0 + 17 * this.dpr, y);
      y += 13 * this.dpr;
    }
    ctx.restore();
  }

  private drawSectionLegend(): void {
    const { ctx } = this;
    const H = this.canvas.height;
    const big = this.expanded || this.mainMode;

    const pad = 8 * this.dpr;
    const rowH = (big ? 17 : 14) * this.dpr;
    const sampleLen = (big ? 30 : 22) * this.dpr;
    const gap = 7 * this.dpr;
    const fontPx = Math.round((big ? 11 : 9.5) * this.dpr);

    ctx.font = `${fontPx}px ui-monospace, monospace`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";

    const rows = SECTION_TYPE_ORDER;
    const blockH = rows.length * rowH + (big ? 16 : 12) * this.dpr;
    // Anchor above the ground/footer band, bottom-left gutter.
    const x0 = pad + 26 * this.dpr; // clear the height-tick gutter
    let y = H - blockH;

    // Caption. Colour now encodes MATERIAL (see drawMaterialLegend), so this key
    // is style-only: it maps stroke STYLE → section family, in a neutral swatch.
    ctx.fillStyle = TITLE_TEXT;
    ctx.textBaseline = "bottom";
    ctx.fillText("SECTION · STYLE", x0, y - 2 * this.dpr);
    ctx.textBaseline = "middle";

    const neutral = "rgba(200,218,235,0.85)";

    for (const type of rows) {
      const style = SECTION_STYLES[type];
      const cy = y + rowH / 2;
      // Sample width echoes the exaggeration curve at a representative size.
      const sampleDrag =
        type === "guy"
          ? 0.008
          : type === "rod"
            ? 0.024
            : type === "flat"
              ? 0.075
              : type === "angle"
                ? 0.114
                : 0.168; // pipe
      const w = Math.max(1, sectionBaseWidthPx(sampleDrag, type) * this.dpr * 0.85);

      ctx.strokeStyle = neutral;
      ctx.lineCap = style.cap;
      ctx.setLineDash(style.dash.map((d) => d * this.dpr));
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.moveTo(x0, cy);
      ctx.lineTo(x0 + sampleLen, cy);
      ctx.stroke();

      if (style.edge) {
        ctx.setLineDash([]);
        ctx.lineCap = "butt";
        ctx.lineWidth = Math.max(1, this.dpr);
        ctx.beginPath();
        ctx.moveTo(x0, cy + w * 0.5 + this.dpr);
        ctx.lineTo(x0 + sampleLen, cy + w * 0.5 + this.dpr);
        ctx.stroke();
      }

      ctx.setLineDash([]);
      ctx.fillStyle = TICK_TEXT;
      ctx.fillText(style.label, x0 + sampleLen + gap, cy);
      y += rowH;
    }

    ctx.setLineDash([]);
    ctx.lineCap = "round";
  }
}
